/**
 * End Use Chart Component
 *
 * Displays energy consumption by end use category as pie or bar chart.
 */

import { useMemo, useState, useRef, useEffect } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import type { ESOData } from '@/simulation/parsers/types';
import type { EndUseCategory } from '../utils/metrics';
import { calculateEndUseBreakdown } from '../utils/metrics';
import type { UnitSystem } from '../utils/unitConversion';
import { convertValue, getDisplayUnit, formatNumber } from '../utils/unitConversion';
import { exportChartAsPNG, downloadFile } from '../utils/export';

/**
 * Chart type options
 */
type ChartType = 'pie' | 'bar';

/**
 * Props for EndUseChart component
 */
interface EndUseChartProps {
  /** Parsed ESO data */
  data: ESOData;
  /** Unit system (SI or IP) */
  unitSystem?: UnitSystem;
  /** Initial chart type */
  initialChartType?: ChartType;
  /** Chart height in pixels */
  height?: number;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Custom tooltip for pie chart
 */
function PieTooltip({
  active,
  payload,
  unitSystem,
}: {
  active?: boolean;
  payload?: Array<{ payload: EndUseCategory & { displayValue: number } }>;
  unitSystem: UnitSystem;
}) {
  if (!active || !payload || payload.length === 0) return null;

  const item = payload[0]?.payload;
  if (!item) return null;

  const displayUnit = getDisplayUnit('kWh', unitSystem);

  return (
    <div className="bg-card border border-border rounded-lg p-3 shadow-xl">
      <p className="font-medium text-foreground">{item.name}</p>
      <p className="text-sm text-muted-foreground mt-1">
        {formatNumber(item.displayValue, 1)} {displayUnit}
      </p>
      <p className="text-sm text-muted-foreground">{item.percentage.toFixed(1)}%</p>
    </div>
  );
}

/**
 * Custom tooltip for bar chart
 */
function BarTooltip({
  active,
  payload,
  label,
  unitSystem,
}: {
  active?: boolean;
  payload?: Array<{ value: number; payload: EndUseCategory & { displayValue: number } }>;
  label?: string;
  unitSystem: UnitSystem;
}) {
  if (!active || !payload || payload.length === 0) return null;

  const item = payload[0];
  if (!item) return null;

  const displayUnit = getDisplayUnit('kWh', unitSystem);

  return (
    <div className="bg-card border border-border rounded-lg p-3 shadow-xl">
      <p className="font-medium text-foreground">{label}</p>
      <p className="text-sm text-muted-foreground mt-1">
        {formatNumber(item.value, 1)} {displayUnit}
      </p>
    </div>
  );
}

/**
 * End Use Chart Component
 */
export function EndUseChart({
  data,
  unitSystem = 'SI',
  initialChartType = 'pie',
  height = 300,
  className = '',
}: EndUseChartProps) {
  const [chartType, setChartType] = useState<ChartType>(initialChartType);
  const chartRef = useRef<HTMLDivElement>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const errorTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
    };
  }, []);

  // Calculate end use breakdown
  const endUseData = useMemo(() => {
    const breakdown = calculateEndUseBreakdown(data);

    // Convert values based on unit system
    return breakdown.map((item) => ({
      ...item,
      displayValue: convertValue(item.valueKWh, 'kWh', unitSystem),
    }));
  }, [data, unitSystem]);

  const displayUnit = getDisplayUnit('kWh', unitSystem);
  const totalEnergy = useMemo(
    () => endUseData.reduce((sum, item) => sum + item.displayValue, 0),
    [endUseData]
  );

  // Export handlers
  const handleExportPNG = async () => {
    if (chartRef.current) {
      setExportError(null);
      // Clear any existing timeout
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
        errorTimeoutRef.current = null;
      }
      try {
        await exportChartAsPNG(chartRef.current, 'end-use-breakdown.png');
      } catch (error) {
        console.error('Failed to export PNG:', error);
        const message = error instanceof Error ? error.message : 'Unknown error';
        setExportError(`Failed to export PNG: ${message}`);
        // Auto-clear error after 5 seconds
        errorTimeoutRef.current = setTimeout(() => { setExportError(null); }, 5000);
      }
    }
  };

  const handleExportCSV = () => {
    const rows = ['End Use,Energy (kWh),Percentage'];
    for (const item of endUseData) {
      rows.push(`${item.name},${item.displayValue.toFixed(2)},${item.percentage.toFixed(1)}%`);
    }
    downloadFile(rows.join('\n'), 'end-use-breakdown.csv');
  };

  if (endUseData.length === 0) {
    return (
      <div className={`bg-card rounded-lg p-4 ${className}`}>
        <h3 className="text-lg font-semibold text-foreground mb-4">End Use Breakdown</h3>
        <div className="text-center text-muted-foreground py-8">
          No end use data available
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-card rounded-lg p-4 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-foreground">End Use Breakdown</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Total: {formatNumber(totalEnergy, 1)} {displayUnit}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Chart type toggle */}
          <div className="flex bg-secondary rounded-lg p-1">
            <button
              onClick={() => { setChartType('pie'); }}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                chartType === 'pie'
                  ? 'bg-muted text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Pie
            </button>
            <button
              onClick={() => { setChartType('bar'); }}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                chartType === 'bar'
                  ? 'bg-muted text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Bar
            </button>
          </div>

          {/* Export buttons */}
          <button
            onClick={handleExportCSV}
            className="px-3 py-1 text-sm bg-secondary text-muted-foreground rounded hover:bg-muted transition-colors"
            title="Export as CSV"
          >
            CSV
          </button>
          <button
            onClick={handleExportPNG}
            className="px-3 py-1 text-sm bg-secondary text-muted-foreground rounded hover:bg-muted transition-colors"
            title="Export as PNG"
          >
            PNG
          </button>
        </div>
      </div>

      {/* Export Error Message */}
      {exportError && (
        <div className="mb-3 px-3 py-2 bg-destructive/10 text-destructive text-sm rounded-md flex items-center justify-between">
          <span>{exportError}</span>
          <button
            onClick={() => { setExportError(null); }}
            className="ml-2 text-destructive hover:text-destructive/80"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Chart */}
      <div ref={chartRef} style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          {chartType === 'pie' ? (
            <PieChart>
              <Pie
                data={endUseData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={2}
                dataKey="displayValue"
                nameKey="name"
                label={(props: { name?: string; percentage?: number; payload?: { percentage?: number } }) => {
                  const pct = props.payload?.percentage ?? props.percentage ?? 0;
                  return pct > 5 ? `${props.name ?? ''} (${pct.toFixed(0)}%)` : '';
                }}
                labelLine={false}
              >
                {endUseData.map((entry, index) => (
                  <Cell key={`cell-${String(index)}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<PieTooltip unitSystem={unitSystem} />} />
              <Legend
                layout="vertical"
                align="right"
                verticalAlign="middle"
                formatter={(value: string) => (
                  <span className="text-muted-foreground text-sm">{value}</span>
                )}
              />
            </PieChart>
          ) : (
            <BarChart
              data={endUseData}
              layout="vertical"
              margin={{ left: 100, right: 20, top: 10, bottom: 10 }}
            >
              <XAxis
                type="number"
                tick={{ fill: '#9ca3af', fontSize: 12 }}
                tickFormatter={(value: number) => formatNumber(value, 0)}
              />
              <YAxis
                type="category"
                dataKey="name"
                tick={{ fill: '#9ca3af', fontSize: 12 }}
                width={90}
              />
              <Tooltip content={<BarTooltip unitSystem={unitSystem} />} />
              <Bar dataKey="displayValue" radius={[0, 4, 4, 0]}>
                {endUseData.map((entry, index) => (
                  <Cell key={`cell-${String(index)}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default EndUseChart;
